import { getQuickSubmitCandidates } from '../../../../services/candidatesApi';
import { getJobList } from '../../../../services/dropdownApi';
import {
  checkSubmissionExists,
  checkSubmissionsExistBulk,
  searchQuickSubmitJobs,
  searchQuickSubmitCandidates,
} from '../../../../services/popupApi';
import { relativeDateLabel } from '../../dynamic/utils/popupUtils';
import { matchRelevantSkills } from '../../../../services/skillMatchApi';
import { stashPendingPromise } from '../../../../services/pendingHandoff';
import { checkInProgress } from '../../../form/inProgressGate';
import { getModuleAutomations } from '../../../../services/adminApi';

const ALREADY_SUBMITTED_LABEL = 'Already submitted';

// The two modules this popup moves a record BETWEEN. The gate below asks the
// TARGET module's rules about the SOURCE module's record — a candidate's own
// rules are what allowed the unfinished qualification to be recorded in the
// first place, so asking them here would always say yes.
const GATE_TARGET_MODULE = 'submissions';
const GATE_SOURCE_MODULE = 'candidates';
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const STALE_CANDIDATE_DAYS = 90;
const STALE_CANDIDATE_MESSAGE = 'This candidate was created or last updated more than 90 days ago. Only candidates created or updated within the last 90 days can be selected. Please update this candidate first.';
const CANDIDATE_CREATED_DATE_KEYS = ['createdAt', 'createdDate', 'dateCreated', 'created_on', 'createdOn'];
const CANDIDATE_UPDATED_DATE_KEYS = ['updatedAt', 'updatedDate', 'lastUpdatedAt', 'lastUpdatedDate', 'modifiedAt', 'modifiedDate'];

function getCandidateDate(row, keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (!value) continue;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return null;
}

function getCandidateCreatedAt(row) {
  return getCandidateDate(row, CANDIDATE_CREATED_DATE_KEYS);
}

function getCandidateUpdatedAt(row) {
  return getCandidateDate(row, CANDIDATE_UPDATED_DATE_KEYS);
}

function fullDaysAgo(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
  const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return Math.max(0, Math.floor((startOfDay(new Date()) - startOfDay(value)) / MS_PER_DAY));
}

function monthsAgo(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
  const now = new Date();
  let months = (now.getFullYear() - value.getFullYear()) * 12 + (now.getMonth() - value.getMonth());
  if (now.getDate() < value.getDate()) months -= 1;
  return Math.max(0, months);
}

function yearsAgo(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - value.getFullYear();
  if (
    now.getMonth() < value.getMonth()
    || (now.getMonth() === value.getMonth() && now.getDate() < value.getDate())
  ) {
    years -= 1;
  }
  return Math.max(0, years);
}

function getCreatedAgoTone(daysAgo) {
  if (daysAgo === null) return '';
  if (daysAgo <= 14) return 'green';
  if (daysAgo <= 89) return 'yellow';
  return 'red';
}

export function isCandidateStale(row, days = STALE_CANDIDATE_DAYS) {
  if (!row) return false;
  const cutoff = Date.now() - days * MS_PER_DAY;
  const createdAt = getCandidateCreatedAt(row);
  const updatedAt = getCandidateUpdatedAt(row);
  const createdFresh = createdAt && createdAt.getTime() >= cutoff;
  const updatedFresh = updatedAt && updatedAt.getTime() >= cutoff;
  return !createdFresh && !updatedFresh;
}

function getCreatedAgoParts(row) {
  const updatedAt = getCandidateUpdatedAt(row);
  if (!updatedAt) return { createdAgo: '', createdAgoTone: '' };

  const daysAgo = fullDaysAgo(updatedAt);
  const months = monthsAgo(updatedAt);
  const years = yearsAgo(updatedAt);

  if (years >= 1) {
    return {
      createdAgo: `${years} year${years === 1 ? '' : 's'} ago`,
      createdAgoTone: getCreatedAgoTone(daysAgo),
    };
  }

  if (months >= 1) {
    return {
      createdAgo: `${months} month${months === 1 ? '' : 's'} ago`,
      createdAgoTone: getCreatedAgoTone(daysAgo),
    };
  }

  return {
    createdAgo: relativeDateLabel(updatedAt),
    createdAgoTone: getCreatedAgoTone(daysAgo),
  };
}

function withCreatedAgo(row) {
  return {
    ...row,
    ...getCreatedAgoParts(row),
    isStaleCandidate: isCandidateStale(row),
  };
}

function candidateIdOf(row) {
  return String(row?.applicantId ?? row?._id ?? row?.id ?? '');
}

// The edit route is CONFIG, never guessed. There is no derivable rule: this app
// routes candidates at "/candidate/edit/:id" (SINGULAR) and clients at
// "/clients/edit/:id" (plural). This function used to build the plural form,
// which matches no route — so the catch-all redirected to the dashboard and the
// candidate the user asked to update simply vanished.
//
// It reads the same modules.strategies.duplicateCheck.editRoute the Add form's
// duplicate dialog uses, so there is ONE configured answer for "where do I edit
// a candidate" rather than two that can disagree. Cached: it is config, and the
// value is identical for every row in the list.
let candidateEditRoutePromise = null;

async function getCandidateEditRoute() {
  if (!candidateEditRoutePromise) {
    candidateEditRoutePromise = getModuleAutomations(GATE_SOURCE_MODULE)
      .then((cfg) => cfg?.strategies?.duplicateCheck?.editRoute ?? '')
      .catch(() => '');
  }
  return candidateEditRoutePromise;
}

async function getCandidateEditPath(row) {
  const candidateId = candidateIdOf(row);
  if (!candidateId) return '';
  const template = await getCandidateEditRoute();
  if (!template) return '';
  return template.replace(/:(_?id)\b/g, encodeURIComponent(candidateId));
}

export async function guardQuickSubmitCandidateSelection({ option, popupContext, confirm }) {
  const row = option?._raw ?? option ?? {};
  const candidateIdForGate = candidateIdOf(row);

  // Region gate first, and reported HERE rather than only on submit: the user
  // has just picked a person, and telling them now — while the list is still
  // open and they can pick someone else — is the difference between a dead end
  // and a choice. submit() checks again, because a guard is a courtesy and the
  // popup can be driven without one.
  if (candidateIdForGate) {
    const gate = await checkInProgress({
      targetModule: GATE_TARGET_MODULE,
      sourceModule: GATE_SOURCE_MODULE,
      recordId: candidateIdForGate,
    });
    if (gate.severity === 'block') {
      return new Promise((resolve) => {
        confirm({
          title: 'This candidate cannot be submitted',
          content: gate.message,
          okText: 'Pick someone else',
          cancelText: 'Close',
          onOk: () => resolve(false),
          onCancel: () => resolve(false),
        });
      });
    }
  }

  if (!isCandidateStale(row)) return true;

  const navigate = popupContext?.navigate;
  const candidateId = candidateIdForGate;
  if (!navigate || !candidateId) return false;

  return new Promise((resolve) => {
    confirm({
      title: 'Candidate update required',
      content: STALE_CANDIDATE_MESSAGE,
      okText: 'OK',
      cancelText: 'Cancel',
      onOk: async () => {
        // No configured route means we do NOT navigate. Closing the dialog and
        // leaving the user where they are is recoverable; throwing them at a
        // URL that matches nothing is not.
        const path = await getCandidateEditPath(row);
        if (path) navigate(path);
        resolve(false);
      },
      onCancel: () => resolve(false),
    });
  });
}

function markSubmitted(option, submissionId, submittedAt) {
  return {
    ...option,
    label: `${option.label} - ${ALREADY_SUBMITTED_LABEL}`,
    disabled: true,
    title: `${ALREADY_SUBMITTED_LABEL}${submissionId ? ` (submission ${submissionId})` : ''}`,
    alreadySubmitted: true,
    existingSubmissionId: submissionId || '',
    ...(submittedAt ? { submittedAt } : {}),
  };
}

export const api = {
  searchOptions: async (field, search, context) => {
    if (field.searchFetchKey === 'quickSubmitJobList') {
      const record = context.selectedRecords?.[0] ?? {};
      const candidateId = record.id ?? record._id ?? context.selectedIds?.[0];

      let list;
      let flaggedByGateway = true;
      try {
        list = await searchQuickSubmitJobs({ searchQuery: search, candidateId });
      } catch {
        flaggedByGateway = false;
        const response = await getJobList({ searchQuery: search, candidateId });
        list = Array.isArray(response?.data) ? response.data : [];
      }

      const options = list.map((item) => ({
        label:
          [item.jobReferenceId ?? item.clientJobReferenceId, item.jobTitle].filter(Boolean).join(' - ')
          || String(item._id ?? ''),
        value: item._id ?? '',
        _raw: item,
      }));

      if (flaggedByGateway) {
        return options.map((opt) => {
          if (opt._raw?.isSubmitted || opt._raw?.alreadySubmitted) {
            return markSubmitted(
              opt,
              opt._raw?.jobSubmissionId || '',
              opt._raw?.submittedAt || '',
            );
          }
          if (opt._raw?.disable) {
            const note = String(opt._raw?.message ?? '').trim();
            return { ...opt, disabled: true, label: note ? `${opt.label} - ${note}` : opt.label, title: note };
          }
          return opt;
        });
      }

      const submitted = await checkSubmissionsExistBulk({
        applicantId: candidateId,
        jobIds: options.map((opt) => opt.value),
      }).catch(() => new Map());

      return options.map((opt) => {
        const hit = submitted.get(String(opt.value));
        return hit ? markSubmitted(opt, hit.submissionId, hit.submittedAt) : opt;
      });
    }

    if (field.searchFetchKey === 'quickSubmitCandidates') {
      const record = context.selectedRecords?.[0] ?? {};
      const jobId =
        context.jobId ?? record.jobId ?? record.id ?? record._id ?? context.selectedIds?.[0];

      let list;
      let submitted = new Map();
      try {
        list = await searchQuickSubmitCandidates({ search, jobId });
      } catch {
        const rows = await getQuickSubmitCandidates({
          isFromSubmission: context.isFromSubmission,
          jobId,
          submissionId: context.submissionId,
          search,
        });
        list = Array.isArray(rows) ? rows : [];

        submitted = await checkSubmissionsExistBulk({
          jobId,
          applicantIds: list.map(candidateIdOf),
        }).catch(() => new Map());
      }

      return list
        .map((row) => withCreatedAgo(row))
        .map((row) => {
          const hit = submitted.get(candidateIdOf(row));
          const isSubmittedHere = Boolean(hit) || row?.isSubmitted === true || row?.alreadySubmitted === true;
          if (!isSubmittedHere) return row;

          const blockingId = hit?.submissionId || row?.jobSubmissionId || '';
          const submittedAt = hit?.submittedAt || row?.submittedAt || '';
          return {
            ...row,
            disabled: true,
            optionNote: ALREADY_SUBMITTED_LABEL,
            isSubmitted: true,
            alreadySubmitted: true,
            existingSubmissionId: blockingId,
            ...(submittedAt ? { submittedAt } : {}),
          };
        });
    }
    return [];
  },

  submit: async (values, context, submitPayload) => {
    const { navigate, selectedRecords = [], selectedIds = [], moduleName } = context;
    const record = selectedRecords[0] ?? {};

    let jobId;
    let candidateId;
    if (context.jobId) {
      jobId = context.jobId;
      candidateId = record.id ?? record._id ?? selectedIds[0] ?? '';
    } else if (moduleName === 'candidate') {
      jobId = values.jobId ?? '';
      candidateId = record.id ?? record._id ?? selectedIds[0] ?? '';
    } else {
      jobId = record.jobId ?? record.id ?? record._id ?? selectedIds[0] ?? '';
      candidateId = values.candidate ?? '';
    }

    const candidateOption = submitPayload?.dynamicOptions?.candidate
      ?.find((opt) => candidateIdOf(opt?._raw) === String(candidateId));
    const jobOption = submitPayload?.dynamicOptions?.jobId
      ?.find((opt) => String(opt?.value) === String(jobId));
    const preMarked = candidateOption?._raw?.isSubmitted
      || candidateOption?._raw?.alreadySubmitted
      || jobOption?.alreadySubmitted
      || jobOption?._raw?.isSubmitted
      || jobOption?._raw?.alreadySubmitted;

    if (!jobId || !candidateId) {
      throw new Error('Select both a job and a candidate to quick submit.');
    }

    if (isCandidateStale(candidateOption?._raw)) {
      throw new Error(STALE_CANDIDATE_MESSAGE);
    }

    // The authoritative region gate. Every route into Quick Submit converges
    // here — picking a job from a candidate, picking a candidate from a job,
    // and the autoSubmitWhenNoFields path that opens no dialog at all — so this
    // is the only place that can cover all three.
    //
    // Without it the flow was: the popup accepts the candidate, the submission
    // form opens, prefills their education off the candidate record, and then
    // refuses to submit. The user is left on a form that will not go through,
    // for a reason nothing on screen explains.
    const gate = await checkInProgress({
      targetModule: GATE_TARGET_MODULE,
      sourceModule: GATE_SOURCE_MODULE,
      recordId: candidateId,
    });
    if (gate.severity === 'block') {
      throw new Error(gate.message);
    }

    let exists = preMarked;
    let blockingSubmissionId = candidateOption?._raw?.existingSubmissionId
      || candidateOption?._raw?.jobSubmissionId
      || jobOption?.existingSubmissionId
      || jobOption?._raw?.jobSubmissionId
      || '';
    try {
      const check = await checkSubmissionExists({ jobId, applicantId: candidateId });
      exists = exists || !!check?.exists;
      blockingSubmissionId = check?.submissionId || blockingSubmissionId;
    } catch (err) {
      throw new Error(
        `Could not verify existing submissions (${err?.message ?? 'request failed'}). Please try again.`,
        { cause: err },
      );
    }

    if (exists) {
      throw new Error(
        `This candidate is already submitted for this job${blockingSubmissionId ? ` (submission ${blockingSubmissionId})` : ''}.`,
      );
    }

    const params = new URLSearchParams();
    if (candidateId) params.set('candidateId', candidateId);

    const priorSubmissionId = candidateOption?.priorSubmissionId
      || candidateOption?._raw?.submissionId
      || jobOption?.priorSubmissionId
      || jobOption?._raw?.submissionId
      || '';

    if (priorSubmissionId) {
      params.set('submissionId', String(priorSubmissionId));
      params.set('isFromSubmission', 'true');
    } else {
      params.set('isFromSubmission', 'false');
    }

    // Kick the relevant-skill match off HERE, at submit, and navigate without
    // waiting for it. Both ids are known and validated at this point, so the
    // request overlaps the form's own mount + prefill instead of adding its
    // latency on top. The form receives the token and fills the rows when the
    // promise settles; it never blocks on it, and matchRelevantSkills resolves
    // to [] on any failure, so a slow or down AI service just means the
    // recruiter fills Relevant Skills by hand exactly as today.
    //
    // The promise itself CANNOT ride in navigate's `state` — react-router
    // writes it via history.pushState, which requires structured-cloneable
    // data, and a Promise is not cloneable (this used to throw
    // "#<Promise> could not be cloned" and abort the navigation outright).
    // stashPendingPromise hands back a plain string token instead.
    const relevantSkillsToken = stashPendingPromise(
      matchRelevantSkills({ jobId, applicantId: candidateId }).catch(() => []),
    );

    navigate(`/submission-create/${jobId}?${params.toString()}`, {
      state: { relevantSkillsToken },
    });
    return { navigated: true };
  },
};
